<template>
	<div v-if="cartProducts.length > 0" class="no-scroll">
		<upgrade-deal-to-menu-dialog
			:dialogOpened="showUpgradeDealToMenuDialog"
			:onClose="() => showUpgradeDealToMenuDialog = false"
			:onConfirm="upgradeDealToMenu"
			:header="$t('shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.deals.title')"
			:text="$t('shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.deals.text')"
			:question="`
					${$t('shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.deals.question', {
						upgradeToItemName: upgradeToMenuData && upgradeToMenuData.upgradeToItemName,
						priceDifference: upgradeToMenuData && formatPriceZero(upgradeToMenuData.priceDifference),
					})}
				`"
			:additionalText="$t('shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.deals.additionalText')"
		/>
		<div class="title-wrapper">
			<div class="basket-title-cancel-order">
				<div class="basket-title" :class="{'long-text': isBasketTitleLong}" data-gtm-basket-title="true">
					{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.shoppingCartEmpty") }}
				</div>
				<div>
					<v-btn primary flat class="btn-color" :ripple="false" name="clear-cart" @click="cancelOrder()">
						<span class="order-button-text"> {{$t("shopComponents.gt3.shoppingCart.shoppingCartWrapper.cancelOrder")}}</span>
						<v-icon>clear</v-icon>
					</v-btn>
				</div>
			</div>
			<div v-if="discountCapped && showDiscountLimitTape" class="cart-discount-limit">
				<div class="cart-discount-limit-info-text">
					<v-icon class="material-icons-outlined discount-limit-info">info</v-icon>
					<div class="discount-limit-text">
						{{ $t("shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.discountTapeSunset") }}<span class="green-text">{{ $t("shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.discountTapePlus") }}</span>
						{{ $t("shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.discountTapeText", {discountMaximum: discountCapped.discountMax}) }}
					</div>
				</div>
				<v-icon class="discount-limit-close-btn" @click="showDiscountLimitTape = false">clear</v-icon>
			</div>
			<div v-if="!featureToggles.favoriteRestaurants || isKioskChannel" class="basket-choice">{{deliveryChoice.title}}</div>
			<div v-else class="basket-choice favourite">
				<div class="basket-choice-restaurant">
					<div>{{deliveryChoice.title}}</div>
					<div>{{$store.getters.globalSelectedShop.shopName}}</div>
				</div>
				<div class="edit-favourite-restaurant" @click="protectOrderFlow(goToShopSelection)">
					<img :src="getImage('basket-edit-icon.svg')" :alt="$t('common.editRestaurant')">
				</div>
			</div>
		</div>
		<div ref="itemWrapper" class="items-wrapper" :class="{ 'items-wrapper-with-voucher': voucherVisible || isVoucher, 'voucher-favourite-restaurant':  featureToggles.favoriteRestaurants, 'redeem-discount-cart': featureToggles.enableDiscountsPage && applicationUserLoggedIn }" @scroll="handleScroll">
			<order-presentation
				:cartProducts="cartProducts"
				:cartOrderTags="cartOrderTags"
				:isProductConfiguring="isProductConfiguring"
				:productCatalog="productCatalog"
				:vouchersRedeemed="vouchersRedeemed"
				:globalSelectedShop="globalSelectedShop"
				:tenantSettings="tenantSettings"
			/>
		</div>
		<div v-if="featureToggles.enableDiscountsPage && applicationUserLoggedIn && !isAtLeastOneDiscountAppliedToCartSelection" class="ready-to-redeem-discounts" @click="isDiscountDialogShown = true">
			<div>{{ $t('utilityComponents.productAsCompensationCRMModule.readyToRedeemDiscounts') }} ({{ this.availableVouchersData.length }})</div>
			<v-icon color="black" class="size-fix">chevron_right</v-icon>
		</div>
		<v-dialog
			v-model="isDiscountDialogShown"
			v-if="isDiscountDialogShown"
			:content-class="`discount-dialog`"
		>
			<v-layout column class="kiosk-modal discount-layout">
				<div>
					<img :src="getImage('arrow-left.png')" class="arrow-size-fix" :alt="$t('common.back')" @click="isDiscountDialogShown = false"/>
					<discount :sideMenuDiscount="true" :onClose="() => isDiscountDialogShown = false"/>
				</div>
			</v-layout>
		</v-dialog>
		<div v-if="shouldShowCarousel && recommendedProductsList.length > 0" class="spinning-wheel-cart">
			<div class="carousel-title">{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.recommendationEngineCarouselTitle") }}</div>
			<carousel
				 ref="carousel"
				 :perPageCustom="[[0, 3], [1920, 2.6]]"
				:mouse-drag="true"
				:scrollPerPage="false"
				:center-mode="true"
				:paginationEnabled="false"
				:autoplay="autoPlayValue"
				@page-change="carouselPageChanged">
				<slide v-for="product in recommendedProductsList" :key="product.id">
					<div class="spinning-wheel-product" @click="addToBasket(product)">
						<div class="spinning-wheel-image-part">
							<img class="spinning-wheel-product-image" :src="backImg(product)[0]" alt="spinning-wheel-product-image">
							<div class="spinning-wheel-product-price">{{ formatPriceZero(getRecommendedProductPrice(product)) }}</div>
						</div>
						<div class="spinning-wheel-product-name">{{ product.name }}</div>
					</div>
				</slide>
			</carousel>
		</div>
		<voucher-input
			v-if="!isAtLeastOneDiscountAppliedToCartSelection && (voucherVisible || isVoucher)"
			:cartProducts="cartProducts"
			:orderLines="orderLines"
			:vouchersRedeemed="vouchersRedeemed"
			:featureToggles="featureToggles"
			:globalSelectedShop="globalSelectedShop"
			:productCatalog="productCatalog"
			:voucherRef="voucherRef"
			@show-keyboard="showKeyboard"
			@hide-keyboard="hideKeyboard"
			:applyVoucherFromModal="applyVoucherFromModal"
		/>
		<v-btn v-if="isKioskChannel && (voucherVisible || isVoucher)" class="add-voucher" @click="showKeyboard">{{$t("utilityComponents.voucherInput.addVoucher")}}</v-btn>
		<sunset-points-input
			v-if="featureToggles.enableSunsetPoints"
		/>
		<confirmation-dialog
			:dialogOpened="dialogOpened"
			:onClose="closeConfirmDialog"
			:onConfirm="currentConfirmAction"
			:isProtectOrderFlow="true"
			:text="$t('shopComponents.defaultGlobalTemplate.orderMessages.orderMessagesWrapper.protectOrderFlow')"
			class="confirmation-dialog2"
		/>
		<v-dialog
			v-model="isKeyboardShown"
			v-if="isKioskChannel && isKeyboardShown"
			:content-class="`keyboard-dialog`"
		>
			<v-layout column class="kiosk-modal keyboard-layout">
				<div>
					<v-btn icon class="close-btn" @click="isKeyboardShown = false">
						<img :alt="$t('common.close')" class="close-image" src="../../../../../static/tid100/images/icons/x-close.svg"/>
					</v-btn>
					<v-layout class="keyboard-dialog-title">
						{{$t("utilityComponents.voucherInput.insertYourCode")}}
					</v-layout>
					<v-layout class="keyboard-dialog-body">
						<v-text-field
							ref="voucherModalInput"
							type="text"
							class="voucher-modal-input"
							v-model="voucherRef"
							required
							:aria-required="true"
							:aria-readonly="false"
							:aria-label="$t('utilityComponents.voucherInput.insertYourCode')"
						></v-text-field>
						<keyboard-input
							v-if="voucherVisible && isKioskChannel && isKeyboardShown"
							:get-image="getImage('delete-number-icon.svg')"
							:keyboard-characters="keyboardCharacters"
							:show-additional-buttons="true"
							@insert-number="insertNumber"
							@delete-last-digit="deleteLastDigit"
							@hide-keyboard="hideKeyboard"
							class="keyboard"
						/>
						<v-btn class="green-btn keyboard-dialog-btn" @click="applyVoucher">
							{{$t("utilityComponents.voucherInput.approve")}}
						</v-btn>
						<v-layout class="keyboard-description">
							{{$t("utilityComponents.voucherInput.insertCodeDescription")}}
						</v-layout>
					</v-layout>
				</div>
			</v-layout>
		</v-dialog>
	</div>
	<div v-else class="shopping-cart-empty no-scroll">
		<div v-if="!featureToggles.verticalKioskBasket" class="title-wrapper margin-bottom">
			<div class="basket-title-cancel-order">
				<v-btn primary flat class="btn-color" :ripple="false" name="clear-cart" @click="cancelOrder()">
					<span class="order-button-text"> {{$t("shopComponents.gt3.shoppingCart.shoppingCartWrapper.cancelOrder")}}</span>
					<v-icon>clear</v-icon>
				</v-btn>
			</div>
			<div v-if="!featureToggles.favoriteRestaurants || isKioskChannel" class="basket-choice">{{deliveryChoice.title}}</div>
			<div v-else class="basket-choice favourite">
				<div class="basket-choice-restaurant">
					<div>{{deliveryChoice.title}}</div>
					<div>{{$store.getters.globalSelectedShop.shopName}}</div>
				</div>
				<div class="edit-favourite-restaurant" @click="protectOrderFlow(goToShopSelection)">
					<img :src="getImage('basket-edit-icon.svg')" :alt="$t('common.editRestaurant')">
				</div>
			</div>
		</div>
		<div class="empty-container">
			<div class="empty-image">
				<img :src=deliveryChoice.href.black :alt="$t('common.emptyCart')">
			</div>
			<div class="empty-message">
				{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.shoppingCartEmpty") }}
			</div>
			<div class="empty-message-description">
				<div>
					{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.shoppingCartEmptyDescription1") }}
				</div>
				<div class="position-bottom">
					{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.shoppingCartEmptyDescription2") }}
				</div>
			</div>
		</div>
		<div v-if="shouldShowCarousel && recommendedProductsList.length > 0" class="spinning-wheel-cart">
			<div class="carousel-title">{{ $t("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.recommendationEngineCarouselTitle") }}</div>
			<carousel
				ref="carousel"
				:perPageCustom="[[0, 3], [1920, 2.6]]"
				:mouse-drag="true"
				:scrollPerPage="false"
				:center-mode="true"
				:paginationEnabled="false"
				:autoplay="autoPlayValue"
				@page-change="carouselPageChanged">
				<slide v-for="product in recommendedProductsList" :key="product.id">
					<div class="spinning-wheel-product" @click="addToBasket(product)">
						<div class="spinning-wheel-image-part">
							<img class="spinning-wheel-product-image" :src="backImg(product)[0]" alt="spinning-wheel-product-image">
							<div class="spinning-wheel-product-price">{{ formatPriceZero(getRecommendedProductPrice(product)) }}</div>
						</div>
						<div class="spinning-wheel-product-name">{{ product.name }}</div>
					</div>
				</slide>
			</carousel>
		</div>
	</div>
</template>

<script>

	import {mapGetters} from "vuex";
	import * as Utils from "../../../../libraries/utils";
	import {pushGTMInternal} from "../../../../integrations/tracking/gtm/pushGtmEvents";
	import {pushGTMeCommerceData} from "../../../../integrations/tracking/gtm/pushGTMeCommerceData";
	import OrderPresentation from "./OrderPresentation-gt3";
	import VoucherInput from "../../../UtilityComponents/VoucherInput";
	import SunsetPointsInput from "../../../UtilityComponents/SunsetPointsInput";
	import appConfig, {channelEnum} from "../../../../appconfig";
	import * as mutationTypes from "../../../../store/mutation-types";
	import ConfirmationDialog from "../../../UtilityComponents/ConfirmationDialog";
	import protectOrderFlowMixin from "../../../../mixins/protectOrderFlow.mixin";
	import KeyboardInput from "../../../UtilityComponents/KeyboardInput.vue";
	import {Carousel, Slide} from "vue-carousel/src";
	import RecommendationEngineProvider from "../../../../providers/RecommendationEngineProvider";
	import {DeliveryMethodsEnum} from "../../../../domains2/libraries/DeliveryMethods";
	import {canBePickedUp, getBackImages, RCMSourceType} from "../../../../libraries/utils";
	import {getProductIdsFromOrderLines} from "../../../../helper/ProductSelectionHelper";
	import {copyObject, formatPriceZero} from "../../../../domains2/libraries/Utils";
	import ProductSelection from "../../../../domains2/orders/ProductSelection";
	import * as actions from "../../../../store/mutation-types";
	import OrderHelperObject from "../../../../domains2/orders/OrderHelperObject";
	import {getProductPriceWithDiscounts} from "../../../../libraries/globalTemplateSpecific/bm100/utils";
	import OrderItemCommonMixin from "../../../../mixins/OrderItemCommon.mixin";
	import voucherAwardMixin from "../../../../mixins/voucherReward/voucherAward.mixin";
	import Discount from "../../gt2/Discount/Discount.vue";
	import productSelectionMixin from "../../../../mixins/productSelection.mixin";
	import dealsMixin from "../../../../mixins/deals.mixin";
	import pricesMixin from "../../../../mixins/prices.mixin";
	import UpgradeDealToMenuDialog from "../../gt2/Dialogs/UpgradeDealToMenuDialog.vue";
	import productCatalogMenuMixin from "../../../../mixins/productCatalogMenu.mixin";
	import {isLongText} from "../../../../helper/LocalizationHelper";
	import {preventAutoHideOldBasket} from "../../../../helper/cartHelper/autoHideOldBasket";
	import HelloRetail from "../../../../integrations/recommendations/helloRetail/HelloRetail";

	export default {
		name: "ShoppingCartCheckoutInit-gt3",
		props: ["deliveryChoice", "isProductConfiguring", "isVoucher"],
		mixins: [
			protectOrderFlowMixin,
			OrderItemCommonMixin,
			voucherAwardMixin,
			productSelectionMixin,
			pricesMixin,
			dealsMixin,
			productCatalogMenuMixin
		],
		components: {
			UpgradeDealToMenuDialog,
			Discount,
			KeyboardInput,
			VoucherInput,
			SunsetPointsInput,
			OrderPresentation,
			ConfirmationDialog,
			Carousel,
			Slide
		},
		data() {
			return {
				voucherRef: "",
				keyboardCharacters: [
					["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
					["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
					["A", "S", "D", "F", "G", "H", "J", "K", "L", "."],
					["Z", "X", "C", "V", "B", "N", "M"]
				],
				isKeyboardShown: false,
				applyVoucherFromModal: false,
				currentCarouselPage: 1,
				pageNumber: 1,
				autoPlayValue: true,
				recommendedProductsList: [],
				productSelection: null,
				receiptLine: null,
				orderLine: null,
				carouselBuffer: 10,
				isDiscountDialogShown: false,
				showDiscountLimitTape: true,
				upgradeToMenuData: null,
				showUpgradeDealToMenuDialog: false
			};
		},
		async created(){
			const self = this;
			if (!self.$store.getters.priceRules || !self.$store.getters.priceRules.length) {
				await self.$store.dispatch(mutationTypes.FETCH_PRICE_RULES);
			}
			if (self.applicationUserLoggedIn && (self.featureToggles.enableSubscription || this.featureToggles.enableSunsetPoints)) {
				await self.$store.dispatch(mutationTypes.GATHER_USER_DATA, {page: this.$route.name});
				await self.setOrderTags();
			}
			if(this.cartProducts.length && this.shouldShowCarousel) {
				this.recommendedProductsList = await this.getRCMProducts();
				if(this.recommendedProductsList.length) {
					this.getCarousel();
				}
			}
			await self.getVouchersData();
		},
		computed: {
			...mapGetters([
				"cartProducts", "totalPrice", "productCatalog", "tenantSettings",
				"getSpecialNotes", "discount", "globalSelectedShop", "orderLines", "customerFetchingSuccess",
				"cartOrderTags", "vouchersRedeemed", "featureToggles", "applicationUserLoggedIn", "subscriptionDetails",
				"tenantSpecificData", "selections", "useCarousel", "experimentId", "recommendedProducts", "useCarousel",
				"discountCapped", "cachedPickupRules"
			]),
			isBasketTitleLong() {
				return isLongText("shopComponents.gt3.shoppingCart.shoppingCartCheckoutInit.shoppingCartEmpty", 12, this.$translate);
			},
			shouldShowCarousel() {
				return this.useCarousel;
			},
			voucherVisible() {
				return this.featureToggles.voucherEnabled;
			},
			staticPath() {
				return Utils.getStaticPath();
			},
			isKioskChannel() {
				return appConfig.channelAlias() === channelEnum.kiosk;
			},
			voucher() {
				return this.voucherRef;
			},
			receiptLines() {
				const self = this;
				return OrderHelperObject.mapToReceiptLines(self.orderLines, self.$i18n);
			},
			recommendedProductPrices() {
				const self = this;
				return getProductPriceWithDiscounts(
					this.recommendedProductsList,
					self.productSelectionsFromAllProducts(this.recommendedProductsList),
					self.productCatalog
				);
			}
		},
		methods: {
			pushGTMInternal: pushGTMInternal,
			handleScroll() {
				preventAutoHideOldBasket();
			},
			cancelOrder() {
				this.$emit("openCancelOrderDialog", true);
			},
			getRecommendedProductPrice(product) {
				if(this.recommendedProductPrices[product.id]) {
					return this.recommendedProductPrices[product.id].discountPrice
						? this.recommendedProductPrices[product.id].discountPrice
						: this.recommendedProductPrices[product.id].basePrice;
				}
				return product.price;
			},
			formatPriceZero,
			getCarousel() {
				setTimeout(() => {
					const carouselHeight = document.querySelector(".spinning-wheel-cart");
					if(this.isKioskChannel) {
						this.carouselBuffer = 15;
					}
					this.$refs.itemWrapper.style = "--custom-height: "+	(carouselHeight.getBoundingClientRect().height+ this.carouselBuffer) +"px";
				}, 100);
			},
			async addToBasket(product) {
				if (this.selections.findIndex(item=>item.productId === product.id) >= 0) {
					this.productSelection = this.selections.find(item=>item.productId === product.id);
					this.orderLine = this.orderLines.find(item => item.productId === product.id);
					this.receiptLine = this.receiptLines.find(item => item.productId === product.id);
					this.product = this.productCatalog.getProduct(product.id);
					await this.changeQuantity(this.productSelection.quantity + 1, true);
					return;
				}
				if(product.departmentId === 2) {
					const item = this.productCatalog.getProduct(product.id);
					const ps = this.createComplexProductSelection(item);
					ps.forceNewInCart = true;
					await this.$store.dispatch(actions.ADD_TO_CART_SINGLE_ITEM, copyObject(ps));
					await this.$store.dispatch(actions.CHECKOUT_RESET_WITH_LAST_UPDATE_TIME);
					return;
				}
				const psOptions = {
					expiration: {
						tenantSettings: this.tenantSettings.productSelectionExpiration,
						tenantConfig: appConfig.tenantConfig.productSelectionExpiration
					}
				};
				const productSelection = ProductSelection.createFromProduct(product, psOptions);
				productSelection.quantity = productSelection.quantity || 1;
				productSelection.rcmSource = RCMSourceType.Carousel;
				this.$store.dispatch(actions.ADD_PRODUCT_SELECTION_TO_CART, productSelection);

				if(this.shouldShowCarousel && this.recommendedProductsList.length && this.experimentId) {
					const pickedRecommendation = this.recommendedProducts.find(p => p.itemNumber === product.id);
					if(pickedRecommendation && pickedRecommendation.trackingCode) {
						HelloRetail.clickTracking(this, pickedRecommendation.trackingCode);
					}
					RecommendationEngineProvider.sendFeedback({
						recommendations: this.recommendedProducts,
						picks: [pickedRecommendation],
						channel: appConfig.channelAlias(),
						shopName: this.globalSelectedShop.shopNameAlias,
						experimentId: this.experimentId
					}, appConfig.tenantId);
					this.$store.commit(mutationTypes.SET_PICKED_RECOMMENDED_PRODUCTS);
				}
				pushGTMeCommerceData({
					eventName: "add_to_cart",
					products: [{product}],
					category: this.productCatalog.getDepartment(product.departmentId),
					value: Utils.getPrice({product})
				});
			},
			async getRCMProducts() {
				const productToRcm = getProductIdsFromOrderLines(this.orderLines).map(id => {
					try {
						const product = this.productCatalog.getProduct(id);
						return {
							itemNumber: product.id,
							name: product.name
						};
					} catch (e) {
						// product is not available, should be ignored
						return false;
					}
				}).filter(Boolean);

				const {recommendations, buffer, experimentId} = await RecommendationEngineProvider.getRecommendations({
					deliveryMethod: Object.keys(DeliveryMethodsEnum)
						.find(key => DeliveryMethodsEnum[key] === this.tenantSpecificData.deliveryChoice),
					products: productToRcm,
					shopName: this.globalSelectedShop.shopNameAlias,
					channel: appConfig.channelAlias(),
					experimentType: "Carousel",
					forceExperimentId: this.experimentId && +this.experimentId ? this.experimentId : null // for case "0"
				}, appConfig.tenantId);
				if(!this.$store.getters.experimentId) {
					this.$store.commit(mutationTypes.SET_EXPERIMENT_ID, experimentId);
				}
				const rawRecommendationsWithBuffer = [
					...(recommendations ? recommendations : []),
					...(buffer ? buffer : [])
				];
				const filteredRecommendedProducts = rawRecommendationsWithBuffer.map(item => {
					// returned products could not be available in Bifrost
					// these should be skipped
					try {
						const product = this.productCatalog.getProduct(item.itemNumber);
						const isProductAvailable = canBePickedUp(
							product,
							this.productCatalog,
							this.$store.getters.cachedPickupRules,
							this.globalSelectedShop.id
						);
						return isProductAvailable ? product : undefined;
					} catch (e) {
						return undefined;
					}
				})
					.filter((product)=> {
						if(!product) {
							return false;
						}
						if(product.options.deliveryMethodIndex) {
							if(!product.options.deliveryMethodIndex.includes(this.tenantSpecificData.deliveryChoice)) {
								return false;
							}
						}
						const categoriesOfProduct = this.productCatalog.getAllCategoriesOfProduct(product.id);
						const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
							(category) => Array.isArray(category.isSpecificForShops) && category.isSpecificForShops.includes(this.globalSelectedShop.id));
						const isProductSupportedByActiveChannel = !categoriesOfProduct.length || categoriesOfProduct.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias())).length;
						if(isProductSupportedByActiveChannel && isProductCategoriesContainSelectedShop) {
							return product;
						}
						return false;
					});
				this.$store.commit(mutationTypes.SET_RECOMMENDED_PRODUCTS, filteredRecommendedProducts.map((p) => {
					const recommendedProduct = rawRecommendationsWithBuffer.find(r => r.itemNumber === p.id);
					return {
						itemNumber: recommendedProduct.itemNumber,
						type: recommendedProduct.type,
						trackingCode: recommendedProduct.trackingCode
					};
				})
				);
				if(this.recommendedProductsList.length) {
					this.getCarousel();
				}
				return (filteredRecommendedProducts || []).slice(0, (this.tenantSettings.maxRecommendedProducts || 6));
			},
			carouselPageChanged() {
				if(!this.$refs.carousel) {
					return;
				}
				if (this.$refs.carousel.slideCount - 2 !== this.$refs.carousel.currentPage) {
					return;
				}
				if(this.autoPlayValue) {
					setTimeout(()=> {
						this.$refs.carousel.goToPage(1, "pagination");
					}, 1000);
				}
				this.autoPlayValue = false;
			},
			showKeyboard() {
				this.isKeyboardShown = true;
				this.applyVoucherFromModal = false;
				setTimeout(() => {
					const voucherInput = this.$refs.voucherModalInput;
					if(voucherInput) {
						voucherInput.focus();
					}
				}, 0);
			},
			hideKeyboard() {
				this.isKeyboardShown = false;
				this.voucherRef = "";
			},
			deleteLastDigit() {
				if(this.voucherRef.length === 1) {
					this.voucherRef = "";
				} else {
					this.voucherRef = this.voucherRef.toString().slice(0, -1);
				}
			},
			insertNumber(number) {
				this.voucherRef = this.voucherRef.toString().concat(number.toString());
			},
			async setOrderTags() {
				return this.$store.dispatch(mutationTypes.RECALCULATE_SUBSCRIPTION_SET_CART_ORDER_TAGS);
			},
			getImage(imageName) {
				return Utils.getMiscImageUrl(imageName);
			},
			goToShopSelection() {
				this.$router.push("/shop-selection");
			},
			applyVoucher() {
				this.isKeyboardShown = false;
				this.applyVoucherFromModal = true;
			},
			backImg(product) {
				return getBackImages(product, this.isKioskChannel);
			},
			productSelectionsFromAllProducts(products) {
				return products.map(product => {
					const productSelection = ProductSelection.createFromProduct(product);
					productSelection.quantity = 1;
					return productSelection;
				});
			}
		},
		watch: {
			"$store.getters.cartProducts.length": async function(val, oldVal) {
				if(val && val !== oldVal && this.shouldShowCarousel && val > 0) {
					this.recommendedProductsList = await this.getRCMProducts();
					this.autoPlayValue = true;
					this.carouselPageChanged();
					if(this.recommendedProductsList.length) {
						this.getCarousel();
					}
				}
				if(val === 0) {
					this.$store.commit(mutationTypes.SET_SELECTED_DEAL_ID);
				}
				setTimeout(() => {
					if(this.isBM100 && this.$store.getters.selectedDealId && val > oldVal) {
						const dealSelection = this.selections.find(s => s.productId === this.$store.getters.selectedDealId);
						if(dealSelection) {
							this.showUpgradeDealToMenuDialog = this.shouldActivateUpgradeToMenu(dealSelection);
						}
					}
				});
			}
		}
	};
</script>
<style scoped lang="scss">
	.v-dialog.keyboard-dialog {
		.keyboard-layout {
			background-image: none;
			margin-top: 50px;
			.close-btn {
				width: 80px;
				height: 80px;
				border-color: #FFFFFF;
				top: 80px;
				right: -10px;
				z-index: 1;
			}
		}
		.keyboard-dialog-title {
			display: flex;
			justify-content: center;
			font-size: 40px;
			padding-bottom: 30px;
		}
		.voucher-modal-input {
			padding: 0;
			margin-bottom: 4px;
			caret-color: #FFFFFF !important;
			/deep/ .v-input__slot {
				//background-color: #171618;
				background-color: #404040;
				height: 70px;
				margin-bottom: 0;
				input {
					max-height: 60px;
					height: 60px;
					font-size: 40px;
					text-align: center;
					color: white;
				}
				&:after {
					border-color: #171618;
				}
			}
			/deep/ .v-text-field__details {
				display: none;
			}

		}
		.keyboard-dialog-body {
			display: flex;
			flex-direction: column;
		}
		.keyboard-dialog-btn {
			height: 70px;
			font-size: 28px;
			text-transform: none;
			margin-top: 4px;
		}
		.keyboard-description {
			justify-content: center;
			padding-top: 30px;
			font-size: 16px;
		}
	}
	.add-voucher {
		opacity: 1;
		border-radius: 5px;
		background-color: #a6a6a6 !important;
		height: 72px;
		width: 100%;
		padding: 16px 24px 18px 24px;
		font-family: Dispatch-Bold;
		font-size: 24px;
		color: #ffffff;
		text-transform: none;
		margin: 22px 0;
		/deep/ .v-btn__content {
			justify-content: flex-start;
		}
	}
	.channel-kiosk.S1 {
		/deep/.v-overlay:before {
			opacity: 0.75;
		}
	}
	.shopping-cart-empty{
		height: calc(100% - 108px);
		.empty-message{
			font-family: Dispatch-Black;
			font-size: 22px;
			font-weight: 900;
			font-stretch: normal;
			font-style: normal;
			line-height: normal;
			letter-spacing: normal;
			text-align: center;
			color: #0d0109;
		}
		.empty-message-description{
			font-family: Dispatch-Light;
			font-size: 20px;
			font-weight: 300;
			font-stretch: normal;
			font-style: normal;
			line-height: normal;
			letter-spacing: normal;
			text-align: center;
			color: #6d7278;
			margin: 32px 73px 0 74px;
			height: 112px;
			position: relative;
			.position-bottom{
				position: absolute;
				bottom: 0;
				left: 0;
				right: 0;
			}
		}
		justify-content: space-evenly!important;
		.title-wrapper.margin-bottom {
			.basket-title-cancel-order {
				justify-content: flex-end;
				.btn-color.v-btn {
					width: auto;
				}
			}
		}
		.title-wrapper:has(.basket-title-cancel-order) {
			border: 0 !important;
		}
	}

	.channel-kiosk .shopping-cart-empty:has(.spinning-wheel-cart) {
		padding-top: 16px !important;
		.margin-bottom {
			margin-bottom: 50px !important;
		}
	}

	.no-scroll{
		padding: 16px 16px 0px 16px;
		height: calc(100% - 108px);
	}
	.margin-bottom{
		margin-bottom: 114px !important;
	}
	.title-wrapper {
		margin-bottom: 16px;
		.basket-title {
			font-family: Dispatch-Black;
			font-size: 22px;
			font-weight: 900;
			font-stretch: normal;
			font-style: normal;
			line-height: normal;
			letter-spacing: normal;
			text-align: left;
			color: #0d0109;
			text-transform: uppercase;
			padding-top: 24px;
		}
		.basket-title.long-text {
			font-size: 18px;
		}
		.basket-choice{
			text-transform: uppercase;
			font-family: Dispatch-Bold;
			padding: 16px;
			background-color: white;
		}
	}
	.channel-kiosk .shopping-cart-wrapper .items-wrapper.items-wrapper-with-voucher {
		--custom-height: 0px;
    height: calc(100% - 217px - var(--custom-height) - var(--limit-height));
	}
	.shopping-cart-wrapper {
		.items-wrapper {
			--limit-height: 0px;
			--custom-height: 0px;
			overflow-y: auto;
			padding-bottom: 30px;
			height: calc(100% - 97px - var(--custom-height) - var(--limit-height));
			&:has(+ .spinning-wheel-cart) {
				--custom-height: 0px;
				height: calc(100% - 117px - var(--custom-height) - var(--limit-height));
			}
			&.items-wrapper-with-voucher {
				height: calc(100% - 196px - var(--custom-height) - var(--limit-height));

				&.voucher-favourite-restaurant {
					height: calc(100% - 217px - var(--custom-height) - var(--limit-height));
				}

				&.redeem-discount-cart {
					height: calc(100% - 277px - var(--custom-height) - var(--limit-height));
				}
			}
		}
		.title-wrapper:has(.cart-discount-limit) + .items-wrapper{
			--limit-height: 64px;
		}
	}
	.no-scroll:has(.items-wrapper.items-wrapper-with-voucher.redeem-discount-cart ~ .sunset-points){
		.redeem-discount-cart {
			--custom-height: 0px;
			height: calc(100% - 307px - var(--custom-height));
		}
	}
	@media (max-width: 1537px) {
		.shopping-cart-wrapper {
			.items-wrapper {
				--limit-height: 0px;
				--custom-height: 0px;
				&.items-wrapper-with-voucher {
					height: calc(100% - 180px - var(--custom-height) - var(--limit-height));
					--custom-height: 0px;
					&.voucher-favourite-restaurant {
						--custom-height: 0px;
						height: calc(100% - 202px - var(--custom-height) - var(--limit-height));
					}
					&.redeem-discount-cart {
						height: calc(100% - 262px - var(--custom-height) - var(--limit-height));
					}
				}
			}
			.title-wrapper:has(.cart-discount-limit) + .items-wrapper{
				--limit-height: 66px;
			}
		}
		.channel-desktop.shop-app {
			.spinning-wheel-cart {
				/deep/ .VueCarousel {
					.VueCarousel-wrapper {
						.spinning-wheel-product {
							padding: 7px;
						}
						.spinning-wheel-product-name {
							margin: 0;
							font-size: 9px;
						}
						.spinning-wheel-add-to-cart {
							height: 28px;
							width: 30px;
							min-width: 30px;
							.v-icon {
								width: 13px;
								height: 14px;
							}
						}
					}
				}
			}
		}
		.no-scroll:has(.items-wrapper.items-wrapper-with-voucher.redeem-discount-cart ~ .sunset-points){
			.redeem-discount-cart {
				--custom-height: 0px;
				height: calc(100% - 292px - var(--custom-height));
			}
		}
	}

	@media (max-width: 1440px) {
		.channel-desktop.shop-app .no-scroll:has(.items-wrapper-with-voucher) > .spinning-wheel-cart {
			padding-bottom: 30px;
		}
	}

	@media (max-width: 1366px) {
		.spinning-wheel-cart {
			display: none;
		}
	}
	@media (max-width: 1025px) {
		.voucher-part {
			&.voucher-empty {
				//height: 82px;
				margin: 0 -10px !important;
				padding: 0 10px !important;
			}
		}
		.shopping-cart-wrapper .items-wrapper{
			&.items-wrapper-with-voucher {
				height: calc(100% - 148px);
				&.voucher-favourite-restaurant {
					height: calc(100% - 180px);
				}
			}
		}
	}

	.no-padding {
		padding: 0 !important;
	}
	.voucher-part {
		position: sticky;
		bottom: 20px;
		background-color: #f6f6f6;

		&.voucher-empty {
			height: 82px;
			border-top: 1px solid #dbd5d5;
			margin: 0 -16px;
			padding: 0 16px;
		}
	}
	.favourite {
		display: flex;
		justify-content: space-between;
		padding-left: 16px;
		padding-right: 16px;
		border: solid 1px #e9e9e9;
		.basket-choice-restaurant div {
			font-size: 16px;
			text-transform: initial;
		}
		.edit-favourite-restaurant {
			width: 32px;
			height: 32px;
			background-color: #EAEAEA;
			border-radius: 3px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-left: 15px;
			cursor: pointer;
		}
	}
	.spinning-wheel-cart {
		/deep/ .VueCarousel-slide {
			display: flex;
		}
		/deep/ .VueCarousel-wrapper {
			overflow: scroll;
		}
		.carousel-title {
			font-size: 17px;
			font-weight: bold;
			text-transform: uppercase;
			margin: 10px 0;
		}
		.spinning-wheel-product {
			background-color: #222222;
			padding: 10px;
			margin-right: 5px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			cursor: pointer;
			.spinning-wheel-image-part {
				position: relative;
			}
			.spinning-wheel-product-image {
				width: 100%;
				height: 100%;
				border: 2px solid #FFD800;
				border-radius: 4px;
			}
			.spinning-wheel-product-name {
				height: 100%;
				font-size: 12px;
				color: white;
				font-weight: bold;
				margin: 8px 0;
			}
			.spinning-wheel-product-price {
				position: absolute;
				top: 6px;
				right: 8px;
				color: white;
				font-size: 13px;
				font-weight: bold;
			}
			.spinning-wheel-add-to-cart {
				border-radius: 3px;
				background-color: #218C74;
				color: #ffffff;
				margin: 0;
				width: 100%;
				padding: 0 5px;
				.v-icon {
					background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNiIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI2IDI4Ij4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPHBhdGggc3Ryb2tlPSIjRkZGIiBzdHJva2Utd2lkdGg9IjIiIGQ9Ik01LjM0NSA3QTIgMiAwIDAgMCAzLjM2IDguNzU3bC0xLjk1NiAxNkEyIDIgMCAwIDAgMy4zODkgMjdoMTkuMjIyYTIgMiAwIDAgMCAxLjk4NS0yLjI0M2wtMS45NTYtMTZBMiAyIDAgMCAwIDIwLjY1NSA3SDUuMzQ1eiIvPgogICAgICAgIDxwYXRoIHN0cm9rZT0iI0ZGRiIgc3Ryb2tlLXdpZHRoPSIyIiBkPSJNOSAxNFY0LjM3NUM5IDEuOTU5IDEwLjc5IDAgMTMgMHM0IDEuOTU5IDQgNC4zNzVWMTQiLz4KICAgICAgICA8cGF0aCBmaWxsPSIjRkZGIiBkPSJNNyAxM2g0djJIN3pNMTUgMTNoNHYyaC00eiIvPgogICAgPC9nPgo8L3N2Zz4K)!important;
					width: 13px;
					height: 14px;
					background-size: contain;
					margin-right: 5px;
				}
				.spinning-wheel-add-to-cart-text {
					font-size: 11px;
					line-height: 1.2;
					font-weight: bold;
					text-align: start;
					white-space: break-spaces;
				}
			}
		}
	}

	.channel-desktop {
		.spinning-wheel-cart {
			/deep/ .VueCarousel {
				.spinning-wheel-product {
					justify-content: start;
					.spinning-wheel-product-name {
						margin: 5px 0;
					}
					.spinning-wheel-product-image {
						height: fit-content;
					}
					.spinning-wheel-product-price {
						top: 6px;
						right: 8px;
						font-size: 10px;
					}
					.spinning-wheel-add-to-cart {
						position: absolute;
						bottom: 11px;
						left: 4px;
						height: 36px;
						width: 40px;
						min-width: 40px;
						.spinning-wheel-add-to-cart-text {
							display: none;
						}
						.v-icon {
							width: 17px;
							height: 19px;
							margin-right: 0;
						}
					}
				}
			}
		}
		.no-scroll:has(.sunset-points) > .spinning-wheel-cart {
			padding-bottom: 40px;
		}
	}
	.ready-to-redeem-discounts {
		margin-bottom: 20px;
		border-radius: 3px;
		border: solid 1px #e9e9e9;
		height: 40px;
		background-color: #fff;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px;
	}

@media (-webkit-device-pixel-ratio: 1.25) {
	.title-wrapper {
		margin-bottom: 8px;
		.basket-choice{
			padding-bottom: 8px;
		}
	}
}
	/deep/ .v-dialog.discount-dialog {
		height: 100vh;
		background-color: black;
		margin: 0 0 0 auto;
		flex-basis: 27%;
		width: 27%;
		border-left: 3px solid #464646;
		overflow: auto;
		.layout.kiosk-modal.discount-layout {
			background-image: none;
			.v-btn.btn-color {
				color: #ffffff;
				width: auto;
			}
		}
		.discount-title.discount-title-desktop {
			text-transform: uppercase;
			font-size: 21px;
			text-align: center;
			padding-bottom: 20px;
			font-weight: normal;
		}
	}
	.no-scroll .title-wrapper .basket-title-cancel-order {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
		.basket-title {
			padding-top: 4px;
		}
		.v-btn.btn-color {
			&:before, &:focus:before, &:hover:before {
				background-color: transparent;
			}
			margin: 0;
			padding: 4px 0 0;
			color: #b5b5b5;
			font-size: 12px;
			text-transform: none;
			letter-spacing: normal;
		}
	}
	.channel-kiosk {
		.no-scroll .title-wrapper .basket-title-cancel-order {
			.v-btn.btn-color {
				font-size: 11px;
				.v-icon {
					font-size: 20px;
				}
			}
		}
	}
	.cart-discount-limit {
		display: flex;
		justify-content: space-between;
		background-color: #202020;
		padding: 10px 16px;
		color: #FFFFFF;
		margin-bottom: 20px;
		.cart-discount-limit-info-text {
			display: flex;
			align-items: center;
			.discount-limit-info {
				color: #FFFFFF;
			}
			.discount-limit-text {
				line-height: 1;
				margin-left: 5px;
				font-size: 13px;
			}
		}
		.discount-limit-close-btn {
			color: #FFFFFF;
		}
		.green-text {
			color: #218C74;
		}
	}
</style>

